import { useState, useEffect, Fragment } from 'react';
import { FileText, RefreshCw, Download, XCircle, CheckCircle2, Clock, AlertCircle, FileSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  enviando: { label: 'Enviando', icon: Clock, color: 'bg-slate-500/10 text-slate-300' },
  processando_autorizacao: { label: 'Processando', icon: Clock, color: 'bg-amber-500/10 text-amber-400' },
  autorizado: { label: 'Autorizada', icon: CheckCircle2, color: 'bg-emerald-500/10 text-emerald-400' },
  cancelado: { label: 'Cancelada', icon: XCircle, color: 'bg-red-500/10 text-red-400' },
  erro: { label: 'Erro', icon: AlertCircle, color: 'bg-red-500/10 text-red-400' },
  erro_autorizacao: { label: 'Rejeitada', icon: AlertCircle, color: 'bg-red-500/10 text-red-400' },
};

function formatMoney(v) {
  return (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function NotasFiscais() {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [integracao, setIntegracao] = useState(null);
  const [empresaApta, setEmpresaApta] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [erroDetalhe, setErroDetalhe] = useState(null);
  const [justificativa, setJustificativa] = useState('');
  const [emitDialog, setEmitDialog] = useState(false);
  const [emitData, setEmitData] = useState({
    destinatario_cpf_cnpj: '', destinatario_nome: '',
    destinatario_email: '', destinatario_municipio: '', destinatario_uf: '',
    natureza_operacao: 'Venda de mercadoria',
    items: [{ descricao: '', quantidade: 1, valor_unitario: 0, ncm: '00000000', cfop: '5102' }],
    informacoes_adicionais: '',
  });

  useEffect(() => {
    loadNotas();
    api.get('/nfe/status-integracao').then((r) => setIntegracao(r.data)).catch(() => {});
    api.get('/nfe/empresa-apta').then((r) => setEmpresaApta(r.data)).catch(() => {});
  }, []);

  const loadNotas = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/nfe/notas');
      setNotas(data);
    } catch {
      toast.error('Erro ao carregar notas fiscais');
    } finally {
      setLoading(false);
    }
  };

  const consultarStatus = async (nota) => {
    try {
      const { data } = await api.get(`/nfe/notas/${nota.id}`);
      setNotas((prev) => prev.map((n) => (n.id === nota.id ? data : n)));
      toast.success(`Status: ${STATUS_META[data.status]?.label || data.status}`);
    } catch {
      toast.error('Erro ao consultar status');
    }
  };

  const verErroCompleto = async (nota) => {
    try {
      const { data } = await api.get(`/nfe/notas/${nota.id}/debug`);
      setErroDetalhe(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar detalhes');
    }
  };

  const downloadArquivo = async (nota, tipo) => {
    try {
      const response = await api.get(`/nfe/notas/${nota.id}/download/${tipo}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `NFe-${nota.numero || nota.id.slice(0, 8)}.${tipo}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erro ao baixar ${tipo.toUpperCase()}`);
    }
  };

  const cancelarNota = async () => {
    if (!cancelDialog) return;
    if (justificativa.length < 15) {
      toast.error('Justificativa deve ter pelo menos 15 caracteres');
      return;
    }
    try {
      await api.post(`/nfe/notas/${cancelDialog.id}/cancelar`, { justificativa });
      toast.success('Nota cancelada');
      setCancelDialog(null);
      setJustificativa('');
      loadNotas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const updateItem = (idx, field, value) => {
    setEmitData((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setEmitData((prev) => ({
      ...prev,
      items: [...prev.items, { _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, descricao: '', quantidade: 1, valor_unitario: 0, ncm: '00000000', cfop: '5102' }],
    }));
  };

  const removeItem = (idx) => {
    setEmitData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const emitirNota = async () => {
    if (!emitData.destinatario_nome || !emitData.destinatario_cpf_cnpj) {
      toast.error('Destinatário: nome e CPF/CNPJ obrigatórios');
      return;
    }
    if (emitData.items.length === 0 || !emitData.items[0].descricao) {
      toast.error('Adicione pelo menos um item');
      return;
    }
    try {
      await api.post('/nfe/emitir', { ...emitData, origem_tipo: 'manual' });
      toast.success('Nota enviada para autorização');
      setEmitDialog(false);
      setEmitData({
        destinatario_cpf_cnpj: '', destinatario_nome: '',
        destinatario_email: '', destinatario_municipio: '', destinatario_uf: '',
        natureza_operacao: 'Venda de mercadoria',
        items: [{ descricao: '', quantidade: 1, valor_unitario: 0, ncm: '00000000', cfop: '5102' }],
        informacoes_adicionais: '',
      });
      loadNotas();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao emitir NFe';
      const formatted = typeof detail === 'string' && detail.includes(';')
        ? detail.replace(/; /g, '\n• ').replace(': ', ':\n• ')
        : detail;
      toast.error(formatted, { duration: 8000 });
    }
  };

  const valorTotalEmit = emitData.items.reduce((acc, i) => acc + (i.quantidade * i.valor_unitario), 0);

  return (
    <div className="p-4 lg:p-8" data-testid="nfe-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Notas Fiscais</h1>
          <p className="text-xs text-slate-500">
            {integracao?.configurado ? (
              <>Integração Focus NFe · <span className={integracao.ambiente === 'producao' ? 'text-red-400' : 'text-amber-400'}>
                {integracao.ambiente === 'producao' ? 'PRODUÇÃO' : 'Homologação'}
              </span></>
            ) : 'Integração não configurada'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadNotas} variant="ghost" className="text-slate-300 hover:bg-white/5" data-testid="refresh-nfe-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
          <Button
            onClick={() => setEmitDialog(true)}
            disabled={empresaApta && !empresaApta.apta}
            title={empresaApta && !empresaApta.apta ? 'Corrija as pendências antes de emitir' : ''}
            className="bg-electric-blue hover:bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="emitir-nfe-btn"
          >
            <FileSignature className="w-4 h-4 mr-2" /> Emitir NFe
          </Button>
        </div>
      </div>

      {/* Banner de diagnóstico — checklist visual */}
      {empresaApta && !empresaApta.apta && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-md p-4 mb-6" data-testid="nfe-diagnostico-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-amber-300 mb-2">
                Empresa &quot;{empresaApta.empresa_nome}&quot; não está apta a emitir NFe
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Corrija os itens abaixo no <span className="text-amber-300">Painel Central → Empresas</span> (acesso do administrador master):
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {(empresaApta.checklist || []).map((item) => (
                  <div key={item.campo} className="flex items-center gap-2 text-xs">
                    {item.ok ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                    )}
                    <span className={item.ok ? 'text-slate-400 line-through' : 'text-slate-200'}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 py-8 text-center">Carregando...</div>
      ) : notas.length === 0 ? (
        <div className="bg-carbon border border-white/5 rounded-md p-12 text-center">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 mb-1">Nenhuma nota fiscal emitida</p>
          <p className="text-xs text-slate-600">Clique em &quot;Emitir NFe&quot; para começar</p>
        </div>
      ) : (
        <div className="bg-carbon border border-white/5 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-obsidian/50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left">Número</th>
                  <th className="px-4 py-3 text-left">Destinatário</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3 text-left">Data</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {notas.map((nota) => {
                  const meta = STATUS_META[nota.status] || STATUS_META.enviando;
                  const Icon = meta.icon;
                  const isErro = ['erro', 'erro_autorizacao'].includes(nota.status);
                  return (
                    <Fragment key={nota.id}>
                    <tr data-testid={`nota-row-${nota.id}`} className="hover:bg-white/5">
                      <td className="px-4 py-3 text-white font-mono">{nota.numero || '—'}</td>
                      <td className="px-4 py-3 text-slate-300">
                        <p className="truncate max-w-[200px]">{nota.destinatario_nome}</p>
                        <p className="text-xs text-slate-500">{nota.destinatario_cpf_cnpj}</p>
                      </td>
                      <td className="px-4 py-3 text-right text-white">{formatMoney(nota.valor_total)}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {nota.created_at ? new Date(nota.created_at).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase font-medium ${meta.color}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          {['enviando', 'processando_autorizacao'].includes(nota.status) && (
                            <Button size="sm" variant="ghost" onClick={() => consultarStatus(nota)} className="h-7 px-2 text-slate-400 hover:text-white" data-testid={`consultar-${nota.id}`}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {nota.caminho_pdf && (
                            <Button size="sm" variant="ghost" onClick={() => downloadArquivo(nota, 'pdf')} className="h-7 px-2 text-electric-blue hover:bg-electric-blue/10" data-testid={`pdf-${nota.id}`}>
                              <Download className="w-3.5 h-3.5 mr-1" />PDF
                            </Button>
                          )}
                          {nota.caminho_xml && (
                            <Button size="sm" variant="ghost" onClick={() => downloadArquivo(nota, 'xml')} className="h-7 px-2 text-slate-300 hover:bg-white/5" data-testid={`xml-${nota.id}`}>
                              <Download className="w-3.5 h-3.5 mr-1" />XML
                            </Button>
                          )}
                          {nota.status === 'autorizado' && (
                            <Button size="sm" variant="ghost" onClick={() => setCancelDialog(nota)} className="h-7 px-2 text-red-400 hover:bg-red-500/10" data-testid={`cancelar-${nota.id}`}>
                              <XCircle className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isErro && (
                            <Button size="sm" variant="ghost" onClick={() => verErroCompleto(nota)} className="h-7 px-2 text-amber-400 hover:bg-amber-500/10" data-testid={`debug-${nota.id}`}>
                              <AlertCircle className="w-3.5 h-3.5 mr-1" />Detalhes
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isErro && nota.erro_mensagem && (
                      <tr className="bg-red-500/5">
                        <td colSpan={6} className="px-4 py-2 text-xs text-red-300">
                          <span className="font-semibold uppercase mr-2">Motivo da rejeição:</span>
                          {nota.erro_mensagem}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialog detalhes do erro */}
      <Dialog open={!!erroDetalhe} onOpenChange={() => setErroDetalhe(null)}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" /> Detalhes do erro SEFAZ
            </DialogTitle>
          </DialogHeader>
          {erroDetalhe && (
            <div className="space-y-3 text-sm">
              <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                <p className="text-xs uppercase text-red-300 mb-1 font-semibold">Mensagem principal</p>
                <p className="text-white">{erroDetalhe.mensagem_principal || '—'}</p>
              </div>
              {erroDetalhe.mensagem_sefaz && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3">
                  <p className="text-xs uppercase text-amber-300 mb-1 font-semibold">Mensagem SEFAZ</p>
                  <p className="text-white">{erroDetalhe.mensagem_sefaz}</p>
                  {erroDetalhe.status_sefaz && (
                    <p className="text-xs text-amber-200 mt-1">Status SEFAZ: {erroDetalhe.status_sefaz}</p>
                  )}
                </div>
              )}
              {erroDetalhe.erros && erroDetalhe.erros.length > 0 && (
                <div>
                  <p className="text-xs uppercase text-slate-400 mb-2 font-semibold">Erros detalhados ({erroDetalhe.erros.length})</p>
                  <div className="space-y-2">
                    {erroDetalhe.erros.map((e, idx) => (
                      <div key={`${e.codigo || 'err'}-${idx}`} className="bg-obsidian border border-white/5 rounded p-2 text-xs">
                        {e.codigo && <span className="text-amber-400 font-mono mr-2">[{e.codigo}]</span>}
                        <span className="text-slate-200">{e.mensagem}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-400 hover:text-white">Resposta bruta da Focus NFe (técnico)</summary>
                <pre className="bg-obsidian rounded p-2 mt-2 overflow-x-auto text-[10px] text-slate-300 max-h-60 overflow-y-auto">
                  {JSON.stringify(erroDetalhe.raw_focus_response, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog cancelar */}
      <Dialog open={!!cancelDialog} onOpenChange={() => { setCancelDialog(null); setJustificativa(''); }}>
        <DialogContent className="bg-carbon border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase">Cancelar NFe</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-400">NFe #{cancelDialog?.numero} — {cancelDialog?.destinatario_nome}</p>
            <div>
              <Label className="text-xs uppercase text-slate-400">Justificativa (mínimo 15 caracteres)</Label>
              <Textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)}
                className="bg-obsidian border-white/10 text-white" rows={3} data-testid="cancel-justificativa" />
              <p className="text-[10px] text-slate-500 mt-1">{justificativa.length}/15</p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <Button variant="ghost" onClick={() => { setCancelDialog(null); setJustificativa(''); }} className="text-slate-400">Fechar</Button>
              <Button onClick={cancelarNota} disabled={justificativa.length < 15} className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50" data-testid="confirmar-cancel">
                Cancelar NFe
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog emitir */}
      <Dialog open={emitDialog} onOpenChange={setEmitDialog}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase">Emitir NFe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-slate-400">Nome/Razão Social *</Label>
                <Input value={emitData.destinatario_nome} onChange={(e) => setEmitData({ ...emitData, destinatario_nome: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" data-testid="emit-nome" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">CPF/CNPJ *</Label>
                <Input value={emitData.destinatario_cpf_cnpj} onChange={(e) => setEmitData({ ...emitData, destinatario_cpf_cnpj: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" data-testid="emit-cpfcnpj" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">E-mail</Label>
                <Input value={emitData.destinatario_email} onChange={(e) => setEmitData({ ...emitData, destinatario_email: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">Natureza da Operação</Label>
                <Input value={emitData.natureza_operacao} onChange={(e) => setEmitData({ ...emitData, natureza_operacao: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" />
              </div>
              <div className="sm:col-span-2 grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs uppercase text-slate-400">Município</Label>
                  <Input value={emitData.destinatario_municipio} onChange={(e) => setEmitData({ ...emitData, destinatario_municipio: e.target.value })}
                    placeholder="Município" className="bg-obsidian border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-400">UF</Label>
                  <Input value={emitData.destinatario_uf} onChange={(e) => setEmitData({ ...emitData, destinatario_uf: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="UF" className="bg-obsidian border-white/10 text-white" maxLength={2} />
                </div>
              </div>
            </div>

            <div className="border-t border-white/5 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase text-slate-400">Itens</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addItem} className="text-electric-blue hover:bg-electric-blue/10 h-7">+ Item</Button>
              </div>
              <div className="space-y-2">
                {emitData.items.map((item, idx) => (
                  <div key={item._key || idx} className="grid grid-cols-12 gap-2 items-start bg-obsidian/50 p-2 rounded border border-white/5" data-testid={`item-${idx}`}>
                    <Input value={item.descricao} onChange={(e) => updateItem(idx, 'descricao', e.target.value)}
                      placeholder="Descrição" className="col-span-12 sm:col-span-5 bg-obsidian border-white/10 text-white text-xs" />
                    <Input type="number" value={item.quantidade} onChange={(e) => updateItem(idx, 'quantidade', parseFloat(e.target.value) || 0)}
                      placeholder="Qtd" className="col-span-3 sm:col-span-1 bg-obsidian border-white/10 text-white text-xs" step="0.01" />
                    <Input type="number" value={item.valor_unitario} onChange={(e) => updateItem(idx, 'valor_unitario', parseFloat(e.target.value) || 0)}
                      placeholder="Valor un." className="col-span-4 sm:col-span-2 bg-obsidian border-white/10 text-white text-xs" step="0.01" />
                    <Input value={item.ncm} onChange={(e) => updateItem(idx, 'ncm', e.target.value)}
                      placeholder="NCM" className="col-span-3 sm:col-span-2 bg-obsidian border-white/10 text-white text-xs" />
                    <Input value={item.cfop} onChange={(e) => updateItem(idx, 'cfop', e.target.value)}
                      placeholder="CFOP" className="col-span-2 sm:col-span-1 bg-obsidian border-white/10 text-white text-xs" />
                    {emitData.items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)} className="col-span-12 sm:col-span-1 text-red-400 h-7 justify-self-end">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-right mt-2 text-sm">
                <span className="text-slate-500">Total: </span>
                <span className="text-white font-semibold">{formatMoney(valorTotalEmit)}</span>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase text-slate-400">Informações Adicionais</Label>
              <Textarea value={emitData.informacoes_adicionais} onChange={(e) => setEmitData({ ...emitData, informacoes_adicionais: e.target.value })}
                className="bg-obsidian border-white/10 text-white" rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
              <Button variant="ghost" onClick={() => setEmitDialog(false)} className="text-slate-400">Cancelar</Button>
              <Button onClick={emitirNota} className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="confirmar-emit">
                <FileSignature className="w-4 h-4 mr-2" /> Emitir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default NotasFiscais;
